import { useState, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import {
    createTicketByEventId,
    showTicketFormUi,
    updateTicketById,
    clearSelectedTicketState
} from '../../../../../../../app/store/slices/ticket/ticket';
import { updateEventById } from '../../../../../../../app/store/slices/event/event';
import useUserStatus from '../../../../../../../hooks/user/useUserStatus';
import useEvent from '../../../../../../../hooks/event-hooks/useEvent';
import { BOOKING } from '../../../../../../../app/store/constant';
import { trimAndCleanSpaces } from '../../../../../../../utils/trimAndCleanSpaces';
import { EVENT_TYPES } from '../../../../../../../app/store/constant';
import { errorToast } from '../../../../../../../utils/toasts/toasts';

// TODO: 03-11-2025 => banking details and invoicing details, commision paid by details are missing. mention in a popup. if user provided gst_number then also add sac code as required fields in popup
// These are the details will be mention in popup that "Your event will be pause due to creating a paid ticket and following details are missing". 1st call will be pause event and 2nd cal will be create ticket

// TODO: Add a drawer that ask who will pay platforom fees if any of the ticket is paid. by default it wil be null.
// TODO: Make it disabled as user submit their selection and enable disabled state in event setting where these two buttons are place in diabled state.
export const validationSchema = Yup.object({
    ticketName: Yup.string().required('Ticket name is required'),
    qty: Yup.number()
        .typeError('Ticket quantity must be a number')
        .required('Ticket quantity is required'),
    price: Yup.number()
        .typeError('Ticket price must be a valid number')
        .required('Ticket price is required'),
    exceedTicketQty: Yup.boolean(),
    isGstApplicable: Yup.boolean(),
    gstRate: Yup.string().when('isGstApplicable', {
        is: true,
        then: (schema) => schema.required('GST rate is required'),
        otherwise: (schema) => schema.nullable()
    }),
    ticketValidDates: Yup.array()
        .min(1, 'Ticket valid dates selection are required')
        .required('Ticket valid dates selection are required'),
    ticketValidSlots: Yup.array(),
    saleStartDate: Yup.string().required('Sale start date is required'),
    saleEndDate: Yup.string().required('Sale end date is required'),
    saleStartTime: Yup.string().required('Sale start time is required'),
    saleEndTime: Yup.string().required('Sale end time is required'),
    maxTicketsPerBooking: Yup.number()
        .typeError('Max booking per ticket must be a number')
        .required('Max tickets per booking is required'),
    isGroupTicket: Yup.boolean(),
    attendeesPerGroupTicket: Yup.number()
        .typeError('Attendees per ticket must be a number')
        .when('isGroupTicket', {
            is: true,
            then: (schema) =>
                schema
                    .required('Attendees per group ticket is required')
                    .min(2, 'Attendees per group ticket must be greater than 1'),
            otherwise: (schema) => schema.nullable()
        }),
    description: Yup.string().required('Ticket description is required'),
    messageToAttendees: Yup.string(),
    checkInTimes: Yup.array()
    // saleStatus: Yup.boolean()
});

export default function useCreateTicket() {
    const dispatch = useDispatch();
    const { eventData, eventSchedules } = useSelector((state) => state.event);
    const { ticket } = useSelector((state) => state.ticket);
    const { gstRates } = useSelector((state) => state.gst);
    const { user } = useSelector((state) => state.auth);
    const [mappedDates, setMappedDates] = useState([]);
    const [mappedSlots, setMappedSlots] = useState([]);
    const [showDatesInInput, setShowDatesInInput] = useState(false);
    const [isSelectAllDates, setIsSelectAllDates] = useState(false);
    const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
    const [showSlotsInInput, setShowSlotsInInput] = useState(false);
    const [isSelectAllSlots, setIsSelectAllSlots] = useState(false);
    const [isSlotDropdownOpen, setIsSlotDropdownOpen] = useState(false);
    const [isEditModeOn, setIsEditModeOn] = useState(false);
    const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
    const [exceedStatusEditable, setExceedStatusEditable] = useState(false);
    const [qtyEditable, setQtyEditable] = useState(false);
    const [shouldAllFieldDisabled, setShouldAllFieldDisabled] = useState(false);

    const [openPaidTicketCheckModal, setOpenPaidTicketCheckModal] = useState(false)
    const [missingDetailsForPaidTicket, setMissingDetailsForPaidTicket] = useState([])

    const userStatus = useUserStatus();
    const eventStatus = useEvent();

    // console.log("Editing States :", {
    //     isEditModeOn: isEditModeOn,
    //     shouldShowEditButton: shouldShowEditButton,
    //     exceedStatusEditable: exceedStatusEditable,
    //     qtyEditable: qtyEditable,
    //     shouldAllFieldDisabled: shouldAllFieldDisabled
    // })

    const initialValues = useMemo(() => {
        const ticketValidDates = [];
        const ticketValidSlots = [];

        ticket?.ticketValidFor?.event_schedule?.forEach((schedule) => {
            if (schedule.type === 'date') {
                // Add directly to ticketValidDates
                ticketValidDates.push({
                    id: schedule.schedule_id,
                    date: dayjs(schedule.date).format('DD/MM/YYYY'),
                    from_time: schedule.from_time,
                    to_time: schedule.to_time
                });
            } else if (schedule.type === 'slot' && schedule.parent_schedule) {
                // Add parent schedule to ticketValidDates if not already added
                const parentDate = {
                    id: schedule.parent_schedule.schedule_id,
                    date: dayjs(schedule.parent_schedule.date).format('DD/MM/YYYY'),
                    from_time: schedule.parent_schedule.from_time,
                    to_time: schedule.parent_schedule.to_time
                };

                if (!ticketValidDates.some((date) => date.id === parentDate.id)) {
                    ticketValidDates.push(parentDate);
                }

                // Add slot to ticketValidSlots
                ticketValidSlots.push({
                    id: schedule.schedule_id,
                    date: dayjs(schedule.parent_schedule.date).format('DD/MM/YYYY'),
                    from_time: schedule.from_time,
                    to_time: schedule.to_time
                });
            }
        });

        return {
            ticketName: ticket?.ticket_name || '',
            qty: ticket?.quantity || null,
            price: ticket?.price || '',
            exceedTicketQty: ticket?.exceed_ticket_qty || false,
            isGstApplicable: ticket?.gst_applicable_on_ticket || false,
            gstRate: ticket?.gst_rate?.id || '',
            ticketValidDates,
            ticketValidSlots,
            saleStartDate: ticket?.ticket_sale_start
                ? dayjs(ticket.ticket_sale_start).format('YYYY-MM-DD')
                : '',
            saleStartTime: ticket?.ticket_sale_start ? dayjs(ticket.ticket_sale_start) : '',
            saleEndDate: ticket?.ticket_sale_end
                ? dayjs(ticket.ticket_sale_end).format('YYYY-MM-DD')
                : '',
            saleEndTime: ticket?.ticket_sale_end ? dayjs(ticket.ticket_sale_end) : '',
            maxTicketsPerBooking: ticket?.customer_limit || BOOKING.maxTicketQtyLimitPerBooking,
            isGroupTicket: ticket?.is_group_booking || false,
            attendeesPerGroupTicket: ticket?.attendees_per_ticket || '',
            description: ticket?.description || '',
            messageToAttendees: ticket?.message_to_attendees || '',
            checkInTimes: []
        };
    }, [ticket]);

    /**
     * id: date/slot -> id
     * checkInTime -> checkInTime of that date/slot
     */

    // console.log("Initial Values -> ", initialValues)

    useEffect(() => {
        if (eventSchedules && eventSchedules.length > 0) {
            const newMappedDates = eventSchedules
                // .filter((item) => item.type === 'date' && item.valid === 1)
                .filter((item) => item.type === 'date')
                .map((item) => ({
                    id: item.id,
                    date: dayjs(item.date).format('DD/MM/YYYY'),
                    from_time: item.from_time,
                    to_time: item.to_time
                }));
            setMappedDates(newMappedDates);
        }
    }, [eventSchedules]);

    // console.log("Mapped Dates -> ", mappedDates)
    // console.log("Mapped Slots -> ", mappedSlots)
    const schedules = useMemo(
        () => ticket?.ticketValidFor?.event_schedule || [],
        [ticket?.ticketValidFor]
    );

    useEffect(() => {
        if (schedules.length > 0) {
            // Check if there are any date type schedules
            const hasDateSchedules = schedules.some((schedule) => schedule.type === 'date');
            setShowDatesInInput(hasDateSchedules);

            // Check if there are any slot type schedules
            const hasSlotSchedules = schedules.some((schedule) => schedule.type === 'slot');
            setShowSlotsInInput(hasSlotSchedules);

            if (hasDateSchedules) {
                setShowDatesInInput(true);
            }
        }
    }, [ticket?.ticketValidFor, showDatesInInput, showSlotsInInput]);


    /**
     * @async
     * @function createOrUpdateTicket
     * @description Creates a new ticket or updates an existing ticket based on the provided values.
     */
    async function createOrUpdateTicket(values) {
        let scheduleIds = [];

        // Calculate scheduleIds based on selection logic
        if (values.ticketValidDates.length === 1) {
            const selectedDate = values.ticketValidDates[0];
            const dateObj = mappedDates.find(
                (date) => date.date === selectedDate.date || date.date === selectedDate
            );

            if (dateObj) {
                const availableSlots = eventSchedules.filter(
                    (item) => item.type === 'slot' && item.parent_date_id === dateObj.id
                );

                if (availableSlots.length > 0) {
                    if (values.ticketValidSlots.length > 0) {
                        if (values.ticketValidSlots.length === availableSlots.length) {
                            scheduleIds = [dateObj.id];
                        } else {
                            scheduleIds = values.ticketValidSlots.map((slot) => slot.id);
                        }
                    } else {
                        scheduleIds = [dateObj.id];
                    }
                } else {
                    scheduleIds = [dateObj.id];
                }
            }
        } else {
            scheduleIds = values.ticketValidDates
                .map((date) => {
                    const dateObj = mappedDates.find(
                        (d) => d.date === date.date || d.date === date
                    );
                    return dateObj ? dateObj.id : null;
                })
                .filter(Boolean);
        }

        const gstRate = values.isGstApplicable ? values.gstRate : 1;
        const saleStartDate = dayjs(values.saleStartDate, 'YYYY-MM-DD');
        const saleStartTime = dayjs(values.saleStartTime, 'HH:mm');
        const saleEndDate = dayjs(values.saleEndDate, 'YYYY-MM-DD');
        const saleEndTime = dayjs(values.saleEndTime, 'HH:mm');

        const saleStart = `${saleStartDate.format('YYYY-MM-DD')} ${saleStartTime.format('HH:mm')}`;
        const saleEnd = `${saleEndDate.format('YYYY-MM-DD')} ${saleEndTime.format('HH:mm')}`;

        const payload = {
            event_id: eventData?.eventId,
            user_id: user?.id,
            price: values.price,
            quantity: values.qty,
            gst_rate_id: gstRate,
            ticket_sale_end: saleEnd,
            ticket_sale_start: saleStart,
            schedule_ids: scheduleIds,
            is_group_booking: values.isGroupTicket,
            exceed_ticket_qty: values.exceedTicketQty,
            customer_limit: values.maxTicketsPerBooking,
            title: trimAndCleanSpaces(values.ticketName),
            gst_applicable_on_ticket: values.isGstApplicable,
            description: trimAndCleanSpaces(values.description),
            attendees_per_ticket: values.isGroupTicket ? values.attendeesPerGroupTicket : 1,
            message_to_attendees: trimAndCleanSpaces(values.messageToAttendees),
            check_in_times: (() => {
                const hasSlotObjects = values.checkInTimes.some(item => item.type === 'slot');
                const filteredCheckInTimes = hasSlotObjects
                    ? values.checkInTimes.filter(item => item.type === 'slot')
                    : values.checkInTimes.filter(item => item.type === 'date');

                return filteredCheckInTimes.map(item => ({
                    id: item.id,
                    check_in_start: item.checkInStart
                }));
            })()
        };

        // Create or update ticket based on presence of ticket_id
        if (ticket?.ticket_id) {
            await dispatch(
                updateTicketById({ ticketId: ticket.ticket_id, ticketData: payload })
            ).unwrap();
            setIsEditModeOn(false);
        } else {
            await dispatch(createTicketByEventId(payload)).unwrap();
        }

        // After creation or update, close the ticket form UI
        dispatch(showTicketFormUi({ showTicketForm: false }));
    };


    async function handlePaidTicketContinue() {
        try {
            // First pause the event
            const eventPayload = {
                step: 'publish',
                user_id: user?.id,
                event_creation_tnc: eventData?.event_creation_tnc,
                publish: false
            };

            await dispatch(updateEventById({
                eventId: eventData?.eventId,
                eventData: eventPayload
            })).unwrap();

            // Then create/update the ticket
            await createOrUpdateTicket(formik.values);

            // Close the modal
            setOpenPaidTicketCheckModal(false);
        } catch (error) {
            errorToast(error?.message || 'An error occurred while processing your request. Please try again.');
        }
    };


    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        validate: (values) => {
            let errors = {};

            const saleStartDate = dayjs(values.saleStartDate, 'DD/MM/YYYY');
            const saleEndDate = dayjs(values.saleEndDate, 'DD/MM/YYYY');
            const saleStartTime = dayjs(values.saleStartTime, 'HH:mm');
            const saleEndTime = dayjs(values.saleEndTime, 'HH:mm');
            const eventEndDate = dayjs(eventData?.end_date, 'DD/MM/YYYY');
            const eventEndTime = dayjs(eventData?.end_time, 'HH:mm');

            if (ticket?.quantity && values.qty < ticket?.quantity) {
                errors.qty = 'Quantity should be greater than previous qty';
            }

            // 1. Sale start date cannot be in the past
            if (saleStartDate.isBefore(dayjs(), 'day')) {
                errors.saleStartDate = 'Sale start date cannot be in the past';
            }

            // 2. Sale start time cannot be in the past
            if (saleStartDate.isBefore(dayjs(), 'day') || saleStartDate.isSame(dayjs(), 'day')) {
                if (saleStartTime.isBefore(dayjs(), 'time')) {
                    errors.saleStartTime = 'Sale start time cannot be in the past';
                }
            }

            // 3. Sale end date cannot be in the past
            if (saleEndDate.isBefore(dayjs(), 'day')) {
                errors.saleEndDate = 'Sale end date cannot be in the past';
            }

            // 4. Sale end time cannot be in the past
            if (saleEndDate.isBefore(dayjs(), 'day') || saleEndDate.isSame(dayjs(), 'day')) {
                if (saleEndTime.isBefore(dayjs(), 'time')) {
                    errors.saleEndTime = 'Sale end time cannot be in the past';
                }
            }

            // 5. sale start date and sale start time must be before event end date and time
            if (saleStartDate.isAfter(dayjs(eventEndDate, 'DD/MM/YYYY'))) {
                errors.saleStartDate = 'Sale start date must be before event end date';
                if (
                    saleStartDate.isAfter(dayjs(eventEndDate, 'DD/MM/YYYY')) &&
                    saleStartTime.isAfter(dayjs(eventEndTime, 'HH:mm'))
                ) {
                    errors.saleStartTime = 'Sale start time must be before event end time';
                }
            }

            // 6. sale end date and time should be before event end date and time
            if (saleEndDate.isAfter(dayjs(eventEndDate, 'DD/MM/YYYY'))) {
                errors.saleEndDate = 'Sale end date must be before event end date';
                if (
                    saleEndDate.isAfter(dayjs(eventEndDate, 'DD/MM/YYYY')) &&
                    saleEndTime.isAfter(dayjs(eventEndTime, 'HH:mm'))
                ) {
                    errors.saleEndTime = 'Sale end time must be before event end time';
                }
            }

            // 7. Sale end date must be after sale start date or same
            if (saleEndDate.isBefore(saleStartDate)) {
                errors.saleEndDate = 'Sale end date must be after sale start date';
            }

            // 8. Sale end time must be after sale start time if sale start date and sale end date is same
            if (saleStartDate.isSame(saleEndDate)) {
                if (saleStartTime.isAfter(saleEndTime) || saleStartTime.isSame(saleEndTime)) {
                    errors.saleEndTime = 'Sale end time must be after sale start time';
                }
            }

            // bigger date from the selected dates list
            const biggerDate =
                values.ticketValidDates[values.ticketValidDates.length - 1]?.toString();

            // 9. Sale start date and end date canot be after the bigger date from the selected dates list
            if (saleStartDate.isAfter(dayjs(biggerDate, 'DD/MM/YYYY'))) {
                errors.saleStartDate = `Sale start date cannot after max date range`;
            }

            if (saleEndDate.isAfter(dayjs(biggerDate, 'DD/MM/YYYY'))) {
                errors.saleEndDate = `Sale end date cannot after max date range`;
            }

            return errors;
        },
        // onSubmit: async (values) => {
        //     try {
        //         // Calculate scheduleIds based on selection logic
        //         let scheduleIds = [];

        //         if (values.ticketValidDates.length === 1) {
        //             // Only one date is selected
        //             const selectedDate = values.ticketValidDates[0];
        //             const dateObj = mappedDates.find(
        //                 (date) => date.date === selectedDate.date || date.date === selectedDate
        //             );

        //             if (dateObj) {
        //                 // Find all slots for this date
        //                 const availableSlots = eventSchedules.filter(
        //                     (item) => item.type === 'slot' && item.parent_date_id === dateObj.id
        //                 );

        //                 if (availableSlots.length > 0) {
        //                     // Date has slots
        //                     if (values.ticketValidSlots.length > 0) {
        //                         // User selected specific slots
        //                         if (values.ticketValidSlots.length === availableSlots.length) {
        //                             // All slots are selected, use the parent date ID
        //                             scheduleIds = [dateObj.id];
        //                         } else {
        //                             // Only specific slots are selected, use their IDs
        //                             scheduleIds = values.ticketValidSlots.map((slot) => slot.id);
        //                         }
        //                     } else {
        //                         // No slots selected even though they exist - invalid state
        //                         // console.error('Date has slots but none were selected');
        //                         scheduleIds = [dateObj.id];
        //                         // return;
        //                     }
        //                 } else {
        //                     // Date has no slots, use the date ID
        //                     scheduleIds = [dateObj.id];
        //                 }
        //             }
        //         } else {
        //             // Multiple dates selected, use all date IDs
        //             scheduleIds = values.ticketValidDates
        //                 .map((date) => {
        //                     const dateObj = mappedDates.find(
        //                         (d) => d.date === date.date || d.date === date
        //                     );
        //                     return dateObj ? dateObj.id : null;
        //                 })
        //                 .filter(Boolean); // Remove any null values
        //         }

        //         const gstRate = values.isGstApplicable ? values.gstRate : 1;

        //         const saleStartDate = dayjs(values.saleStartDate, 'YYYY-MM-DD');
        //         const saleStartTime = dayjs(values.saleStartTime, 'HH:mm');
        //         const saleEndDate = dayjs(values.saleEndDate, 'YYYY-MM-DD');
        //         const saleEndTime = dayjs(values.saleEndTime, 'HH:mm');

        //         const saleStart = `${saleStartDate.format('YYYY-MM-DD')} ${saleStartTime.format('HH:mm')}`;
        //         const saleEnd = `${saleEndDate.format('YYYY-MM-DD')} ${saleEndTime.format('HH:mm')}`;

        //         const payload = {
        //             event_id: eventData?.eventId,
        //             user_id: user?.id,
        //             price: values.price,
        //             quantity: values.qty,
        //             gst_rate_id: gstRate,
        //             ticket_sale_end: saleEnd,
        //             ticket_sale_start: saleStart,
        //             schedule_ids: scheduleIds,
        //             is_group_booking: values.isGroupTicket,
        //             exceed_ticket_qty: values.exceedTicketQty,
        //             customer_limit: values.maxTicketsPerBooking,
        //             title: trimAndCleanSpaces(values.ticketName),
        //             gst_applicable_on_ticket: values.isGstApplicable,
        //             description: trimAndCleanSpaces(values.description),
        //             attendees_per_ticket: values.isGroupTicket ? values.attendeesPerGroupTicket : 1,
        //             message_to_attendees: trimAndCleanSpaces(values.messageToAttendees),
        //             check_in_times: (() => {
        //                 // Check if there are any slot type objects in checkInTimes
        //                 const hasSlotObjects = values.checkInTimes.some(item => item.type === 'slot');

        //                 // If slot objects exist, filter for only slot objects, otherwise use date objects
        //                 const filteredCheckInTimes = hasSlotObjects
        //                     ? values.checkInTimes.filter(item => item.type === 'slot')
        //                     : values.checkInTimes.filter(item => item.type === 'date');

        //                 return filteredCheckInTimes.map(item => ({
        //                     id: item.id,
        //                     check_in_start: item.checkInStart
        //                 }));
        //             })()
        //         };

        //         const eventPayload = {
        //             step: 'publish',
        //             user_id: user?.id,
        //             event_creation_tnc: eventData?.event_creation_tnc,
        //             publish: false
        //         }

        //         if (values.price > 0) {
        //             // check for missing details
        //             const missingDetails = [];

        //             if (!userStatus.hasPanCard) {
        //                 missingDetails.push({
        //                     id: 'panCard',
        //                     title: 'PAN card not provided'
        //                 })
        //             }

        //             if (userStatus.hasGstNumber && !eventStatus.isSACCodeAdded) {
        //                 missingDetails.push({
        //                     id: 'sacCode',
        //                     title: 'SAC code not provided'
        //                 })
        //             }

        //             if (!eventStatus.isBankDetailsAddedForEvent) {
        //                 missingDetails.push({
        //                     id: 'bankDetails',
        //                     title: 'Bank details not provided'
        //                 })
        //             }

        //             if (!eventStatus.isCommissionPaidByEndUser) {
        //                 missingDetails.push({
        //                     id: 'commissionPaidBy',
        //                     title: 'Commission paid by details not provided'
        //                 })
        //             }

        //             if (missingDetails.length > 0) {
        //                 setMissingDetailsForPaidTicket(missingDetails)
        //                 setOpenPaidTicketCheckModal(true)
        //                 return
        //             }

        //         }

        //         if (ticket?.ticket_id) {
        //             await dispatch(
        //                 updateTicketById({ ticketId: ticket.ticket_id, ticketData: payload })
        //             ).unwrap();
        //             setIsEditModeOn(false);
        //         } else {
        //             await dispatch(createTicketByEventId(payload)).unwrap();
        //         }
        //         scheduleIds = [];
        //         dispatch(showTicketFormUi({ showTicketForm: false }));

        //     } catch (error) {
        //         console.error('Error creating/updating ticket:', error);
        //     }
        // }
        onSubmit: async (values) => {
            try {
                if (values.price > 0 && eventStatus.isEventPublishedOnce) {
                    const missingDetails = [];

                    if (eventStatus.hasTaxInvoiceDetailsProvided === 0) {
                        missingDetails.push({
                            id: 'panCard',
                            title: 'PAN card not provided'
                        });
                    }

                    if (userStatus.hasGstNumber && eventStatus.isSACCodeAdded === 0) {
                        missingDetails.push({
                            id: 'sacCode',
                            title: 'SAC code not provided'
                        });
                    }

                    if (eventStatus.isBankDetailsAddedForEvent === 0) {
                        missingDetails.push({
                            id: 'bankDetails',
                            title: 'Bank details not provided'
                        });
                    }

                    if (eventStatus.isCommissionPaidByEndUser === 0) {
                        missingDetails.push({
                            id: 'commissionPaidBy',
                            title: 'Commission paid by details not provided'
                        });
                    }

                    if (missingDetails.length > 0) {
                        setMissingDetailsForPaidTicket(missingDetails);
                        setOpenPaidTicketCheckModal(true);
                        return; // Stop here, let user confirm in popup
                    }
                }

                // If no missing details or price is 0, proceed directly
                await createOrUpdateTicket(values);

            } catch (error) {
                errorToast(error?.message || 'Something went wrong!')
            }
        }
    });

    const { values, setFieldValue, setFieldError } = formik;


    // track the start date is same or not
    const startDateIsSame = useMemo(() => {
        if (!values.ticketValidDates || values.ticketValidDates.length === 0) {
            return false;
        }

        // check conditions
        const dateToCompare =
            EVENT_TYPES.continuous === eventData?.event_type
                ? values.ticketValidDates[0]?.date // if true then take first element of an array
                : values.ticketValidDates[values.ticketValidDates.length - 1]?.date; // else take last element of an array

        // return true or false value
        return dayjs(values.saleStartDate, 'DD/MM/YYYY').isSame(dayjs(dateToCompare, 'DD/MM/YYYY'));
    }, [values.saleStartDate, values.ticketValidDates, eventData?.event_type]);

    // track the end date is same or not
    const endDateIsSame = useMemo(() => {
        if (!values.ticketValidDates || values.ticketValidDates.length === 0) {
            return false;
        }

        const dateToCompare =
            EVENT_TYPES.continuous === eventData?.event_type
                ? values.ticketValidDates[0]?.date // if true then take first element of an array
                : values.ticketValidDates[values.ticketValidDates.length - 1]?.date; // else take last element of an array

        // return true or false value
        return dayjs(values.saleEndDate, 'DD/MM/YYYY').isSame(dayjs(dateToCompare, 'DD/MM/YYYY'));
    }, [values.saleEndDate, values.ticketValidDates, eventData?.event_type]);

    // add minimum time as current minute if sale end date is same as today
    const addMinEndTime = useMemo(() => {
        return dayjs(values.saleEndDate, 'DD/MM/YYYY').isSame(dayjs(), 'day')
            ? dayjs().startOf('minute')
            : dayjs().startOf('day');
    }, [values.saleEndDate]);

    // minimum start time as current minute if sale start date is same as today
    const minStartTime = useMemo(() => {
        return dayjs(values.saleStartDate, 'DD/MM/YYYY').isSame(dayjs(), 'day')
            ? dayjs().startOf('minute')
            : dayjs().startOf('day');
    }, [values.saleStartDate]);

    // minimum end time as sale start time if sale end date is same as sale start date
    const minEndTime = useMemo(() => {
        return dayjs(values.saleStartDate).isSame(values.saleEndDate, 'day')
            ? dayjs(values?.saleStartTime, 'HH:mm')
            : addMinEndTime;
    }, [values.saleStartDate, values.saleEndDate, values.saleStartTime, addMinEndTime]);

    const loadSlots = dayjs(
        values.ticketValidSlots[values.ticketValidSlots.length - 1]?.from_time,
        'HH:mm:ss'
    );

    // find date in event schedule and use its "to_time"
    const findDateInEventSchedule = useMemo(() => {
        // No dates selected, return undefined
        if (!values.ticketValidDates || values.ticketValidDates.length === 0) {
            return null;
        }

        // Determine which date to use based on event type
        const dateToFind =
            EVENT_TYPES.continuous === eventData?.event_type
                ? values.ticketValidDates[0] // For continuous events, use the first date
                : values.ticketValidDates[values.ticketValidDates.length - 1]; // For non-continuous events, use the last date

        // Find the matching date in eventSchedules
        const matchedSchedule = eventSchedules?.find(
            (item) =>
                item.type === 'date' &&
                dayjs(item.date, 'YYYY-MM-DD').isSame(
                    dayjs(dateToFind?.date || dateToFind, 'DD/MM/YYYY')
                )
        );

        // For continuous events, use to_time; for other events, use to_time
        const timeField =
            EVENT_TYPES.continuous === eventData?.event_type
                ? matchedSchedule?.from_time
                : matchedSchedule?.from_time;

        return dayjs(timeField, 'HH:mm:ss');
    }, [values.ticketValidDates, eventSchedules, eventData?.event_type]);

    const dateHasSlots = values.ticketValidSlots.length > 0 ? loadSlots : findDateInEventSchedule;

    const maxStartTime = startDateIsSame
        ? dateHasSlots
        : dayjs(values.saleStartDate, 'DD/MM/YYYY').endOf('day');

    const findEndTimeForDate = (date) => {
        // Get the matched date from schedules
        const matchedDate = eventSchedules?.find(
            (item) =>
                item.type === 'date' &&
                dayjs(item.date, 'YYYY-MM-DD').isSame(dayjs(date, 'DD/MM/YYYY'))
        );

        // If only one date is selected
        if (values.ticketValidDates.length === 1) {
            // Check if any slots are selected
            if (values.ticketValidSlots.length > 0) {
                // Use the to_time of the last selected slot
                const lastSelectedSlot =
                    values.ticketValidSlots[values.ticketValidSlots.length - 1];
                return lastSelectedSlot?.from_time;
            }
        }

        // Check if event type is continuous
        if (eventData?.event_type === EVENT_TYPES.continuous) {
            return matchedDate?.from_time;
        } else {
            return matchedDate?.from_time || '23:59:59';
        }
    };

    // const maxEndTime = endDateIsSame
    //     ? dayjs(findEndTimeForDate(values.saleEndDate), 'HH:mm:ss')
    //     : dayjs(values.saleEndDate, 'DD/MM/YYYY').endOf('day');

    const maxEndTime = endDateIsSame
        ? dayjs(findEndTimeForDate(values.saleEndDate), 'HH:mm:ss').add(30, 'minute')
        : dayjs(values.saleEndDate, 'DD/MM/YYYY').endOf('day').add(30, 'minute');


    // Calculate the maximum valid date range based on event type
    const maxRangeDate = useMemo(() => {
        // If no dates are selected, return null
        if (!values.ticketValidDates || values.ticketValidDates.length === 0) {
            return null;
        }

        // Determine which date to use based on event type
        const dateToUse =
            EVENT_TYPES.continuous === eventData?.event_type
                ? values.ticketValidDates[0]?.date // First date for continuous events
                : values.ticketValidDates[values.ticketValidDates.length - 1]?.date; // Last date for non-continuous events

        // Convert to dayjs object for date operations
        return dayjs(dateToUse, 'DD/MM/YYYY');
    }, [values.ticketValidDates, eventData?.event_type]);

    const minRangeDate = dayjs(values.saleStartDate || null, 'DD/MM/YYYY');

    function preventBackSpaceKey(eventKey) {
        if (eventKey.key === 'Backspace') {
            eventKey.defaultMuiPrevented = true;
        }
    }

    function handleDateSubmission() {
        if (formik.values.ticketValidDates.length > 0) {
            setShowDatesInInput(true);
            setIsSelectAllDates(false);
            setIsDateDropdownOpen(false);
            formik.setFieldValue('ticketValidSlots', []);
        } else {
            setShowDatesInInput(false);
        }
    }

    function handleSlotSubmission() {
        if (formik.values.ticketValidSlots.length > 0) {
            setShowSlotsInInput(true);
            setIsSelectAllSlots(false);
            setIsSlotDropdownOpen(false);
        } else {
            setShowSlotsInInput(false);
        }
    }

    function handleDateChange(event, value) {
        if (eventData?.event_type === 'continuous') {
            // For continuous events, automatically select all dates
            formik.setFieldValue('ticketValidDates', mappedDates);
            setMappedSlots([]);
            formik.setFieldValue('ticketValidSlots', []);
            return;
        }

        // Remove duplicate entries based on id
        const uniqueValues = Array.from(new Map(value.map((item) => [item.id, item])).values());

        // Sort the unique values by id in ascending order
        const sortedValues = [...uniqueValues].sort((a, b) => a.id - b.id);

        // if user selects only one date
        if (sortedValues.length === 1) {
            // filter slots based on the selected date
            const availableSlots = eventSchedules.filter(
                (item) =>
                    // TODO: re-enable the valid check when backend is ready
                    // item.type === 'slot' &&
                    // item.parent_date_id === sortedValues[0].id &&
                    // item.valid === 1

                    item.type === 'slot' &&
                    item.parent_date_id === sortedValues[0].id
            );

            // if there are available slots for the selected date
            if (availableSlots.length > 0) {
                setMappedSlots(availableSlots);
            }
            // if no slots are available for the selected date
            else {
                setMappedSlots([]);
                formik.setFieldValue('ticketValidSlots', []);
            }
        }
        // if user selects multiple dates
        else {
            setMappedSlots([]);
            formik.setFieldValue('ticketValidSlots', []);
        }

        formik.setFieldValue('ticketValidDates', sortedValues);
    }

    function handleSlotChange(event, value) {
        // Sort the values by id in ascending order
        const sortedValues = [...value].sort((a, b) => a.id - b.id);

        formik.setFieldValue('ticketValidSlots', sortedValues);
        setShowSlotsInInput(sortedValues.length > 0);
    }

    function onDateDropdownOpen() {
        setIsDateDropdownOpen(true);
        setShowDatesInInput(false);
    }

    function onSlotDropdownOpen() {
        setIsSlotDropdownOpen(true);
        setShowSlotsInInput(false);
    }

    function handleAllDatesSelect() {
        if (isSelectAllDates) {
            formik.setFieldValue('ticketValidDates', []);
        } else {
            formik.setFieldValue('ticketValidDates', mappedDates);
        }
        setIsSelectAllDates(!isSelectAllDates);
    }

    function handleAllSlotsSelect() {
        if (isSelectAllSlots) {
            formik.setFieldValue('ticketValidSlots', []);
        } else {
            formik.setFieldValue('ticketValidSlots', mappedSlots);
        }
        setIsSelectAllSlots(!isSelectAllSlots);
    }

    function handleCancelTicketCreation() {
        dispatch(showTicketFormUi(false));
        dispatch(clearSelectedTicketState());
        setIsEditModeOn(false);
        formik.resetForm();
    }

    useEffect(() => {
        if (formik.values.ticketValidDates.length === 1) {
            // Filter slots based on the selected date
            const selectedDate = formik.values.ticketValidDates[0];
            const availableSlots = eventSchedules.filter(
                (item) =>
                    // TODO: re-enable the valid check when backend is ready
                    // item.type === 'slot' &&
                    // item.parent_date_id === selectedDate.id &&
                    // item.valid === 1

                    item.type === 'slot' &&
                    item.parent_date_id === selectedDate.id
            );

            if (availableSlots.length > 0) {
                setMappedSlots(availableSlots);
                setShowSlotsInInput(true);
            } else {
                setMappedSlots([]);
                setShowSlotsInInput(false);
            }
        } else {
            setMappedSlots([]);
            setShowSlotsInInput(false);
        }
    }, [formik.values.ticketValidDates, eventSchedules]);

    useEffect(() => {
        if (dayjs(values.saleStartTime, 'HH:mm').isAfter(maxStartTime)) {
            setFieldError('saleStartTime', 'Sale start time cannot be after max time range');
        }
        if (dayjs(values.saleEndTime, 'HH:mm').isAfter(maxEndTime)) {
            setFieldError('saleEndTime', 'Sale end time cannot be after max time range');
        }
    }, [values.saleStartTime, maxStartTime, values.saleEndTime, maxEndTime, setFieldError]);

    useEffect(() => {
        if (values.ticketValidDates.length === 0) {
            setFieldValue('scheduleIds', []);
        }
    }, [values.ticketValidDates, setFieldValue]);

    useEffect(() => {
        if (!ticket) {
            setIsEditModeOn(true);
            setShouldAllFieldDisabled(true);
            setExceedStatusEditable(true);
            setQtyEditable(true);
        }
    }, []);


    useEffect(() => {
        if (ticket) {
            const isOngoing = eventData?.status === 'ongoing';
            const isDrafted = eventData?.status === 'drafted';
            const isScheduled = eventData?.status === 'scheduled';
            const isTicketScheduled = ticket?.sale_status === 'coming_soon';
            const isTicketOngoing = ticket?.sale_status === 'live';
            const eventStatus = isDrafted || isScheduled || isOngoing;
            const ticketStatus = isTicketScheduled || isTicketOngoing;

            const shouldAllowEdit = ticket?.ticket_id && eventStatus && ticketStatus;

            setShouldShowEditButton(shouldAllowEdit);
            setShouldAllFieldDisabled(false);
        }

        // if (ticket === null) {
        //     setShouldShowEditButton(false);
        //     setShouldAllFieldDisabled(false);
        //     setIsEditModeOn(true);
        //     setExceedStatusEditable(false);
        //     setQtyEditable(false);
        // }
    }, [ticket, eventData?.status]);

    function handleEditing() {
        if (ticket) {
            const isTicketSold = ticket?.ticket_sold > 0;

            if (isTicketSold) {
                // If tickets are sold and exceed status is true, don't allow editing those fields
                if (formik.values.exceedTicketQty) {
                    setIsEditModeOn(true);
                    setExceedStatusEditable(false);
                    setQtyEditable(false);
                } else {
                    // If tickets are sold but exceed status is false, allow editing those fields
                    setIsEditModeOn(true);
                    setExceedStatusEditable(true);
                    setQtyEditable(true);
                    setShouldAllFieldDisabled(false);
                }
            } else {
                // If no tickets sold, enable all editing
                setIsEditModeOn(true);
                setExceedStatusEditable(true);
                setQtyEditable(true);
                setShouldAllFieldDisabled(true);
            }
        }
    }

    /**
     * With backend value connection useEffect -> ticket creation + backend value (if exists)
     * @description Effect to update check-in times based on selected ticket valid dates and slots.
     * This effect runs whenever the ticketValidDates or ticketValidSlots in formik values change.
     * It calculates the check-in time for each selected date and slot (30 minutes before the from_time)
     * and updates the formik field 'checkInTimes' with the calculated values.
     * 1. It first checks if there is a saved check_in_start time for each schedule in the ticket data.
     * 2. If a saved time exists, it uses that time; otherwise, it calculates the check-in time
     *    as 30 minutes before the from_time of the date or slot.
     * 3. The calculated or saved check-in times are then stored in the formik field 'checkInTimes'.
     */
    useEffect(() => {
        const checkInTimes = [];

        // Helper to get saved check_in_start for a schedule id from ticket
        const getSavedCheckInStart = (scheduleId) => {
            return ticket?.ticketValidFor?.event_schedule?.find(s => s.schedule_id === scheduleId)?.check_in_start || null;
        };

        // Process dates from ticketValidDates first
        formik.values.ticketValidDates?.forEach((dateItem, index) => {
            // For continuous events, only process the first date
            if (EVENT_TYPES.continuous === eventData?.event_type && index > 0) {
                return; // Skip all dates except the first one for continuous events
            }

            const dateInfo = dateItem;
            if (!dateInfo || !dateInfo.from_time) return;

            const savedCheckInStart = getSavedCheckInStart(dateInfo.id);

            if (savedCheckInStart) {
                const savedTime = dayjs(savedCheckInStart).format('HH:mm');
                checkInTimes.push({
                    id: dateInfo.id,
                    date: dateInfo.date,
                    type: 'date',
                    from_time: dateInfo.from_time,
                    to_time: dateInfo.to_time,
                    checkInTime: savedTime,
                    checkInStart: dayjs(savedCheckInStart).format('YYYY-MM-DD HH:mm:ss')
                });
            } else {
                const checkInTime = dayjs(dateInfo.from_time, 'HH:mm')
                    .subtract(30, 'minute')
                    .format('HH:mm');

                checkInTimes.push({
                    id: dateInfo.id,
                    date: dateInfo.date,
                    type: 'date',
                    from_time: dateInfo.from_time,
                    to_time: dateInfo.to_time,
                    checkInTime,
                    checkInStart: dayjs(dateInfo.date, 'DD/MM/YYYY')
                        .format('YYYY-MM-DD') + ' ' + checkInTime + ':00'
                });
            }
        });

        // Process slots from ticketValidSlots
        formik.values.ticketValidSlots?.forEach(slot => {
            if (!slot || !slot.from_time) return;

            const savedCheckInStart = getSavedCheckInStart(slot.id);

            if (savedCheckInStart) {
                const savedTime = dayjs(savedCheckInStart).format('HH:mm');
                checkInTimes.push({
                    id: slot.id,
                    date: slot.date,
                    type: 'slot',
                    from_time: slot.from_time,
                    to_time: slot.to_time,
                    checkInTime: savedTime,
                    checkInStart: dayjs(savedCheckInStart).format('YYYY-MM-DD HH:mm:ss')
                });
            } else {
                const checkInTme = dayjs(slot.from_time, 'HH:mm')
                    .subtract(30, 'minute')
                    .format('HH:mm');

                checkInTimes.push({
                    id: slot.id,
                    date: slot.date,
                    type: 'slot',
                    from_time: slot.from_time,
                    to_time: slot.to_time,
                    checkInTime: checkInTme,
                    checkInStart: dayjs(slot.date, 'YYYY-MM-DD')
                        .format('YYYY-MM-DD') + ' ' + checkInTme + ':00'
                });
            }
        });
        formik.setFieldValue('checkInTimes', checkInTimes);

    }, [formik.values.ticketValidDates, formik.values.ticketValidSlots, ticket, eventData?.event_type]);

    /**
     * Without backend value connection useEffect -> only for ticket creation
     * @description Effect to update check-in times based on selected ticket valid dates and slots.
     * This effect runs whenever the ticketValidDates or ticketValidSlots in formik values change.
     * It calculates the check-in time for each selected date and slot (30 minutes before the from_time)
     * and updates the formik field 'checkInTimes' with the calculated values.
     */
    // useEffect(() => {
    //     // Update check-in times when dates or slots change
    //     const checkInTimes = [];

    //     // Process dates from ticketValidDates first
    //     formik.values.ticketValidDates?.forEach((dateItem, index) => {
    //         // For continuous events, only process the first date
    //         if (EVENT_TYPES.continuous === eventData?.event_type && index > 0) {
    //             return; // Skip all dates except the first one for continuous events
    //         }

    //         let dateInfo = dateItem;

    //         if (!dateInfo || !dateInfo.from_time) return;

    //         // Calculate check-in time (30 minutes before from_time)
    //         const checkInTime = dayjs(dateInfo.from_time, 'HH:mm')
    //             .subtract(30, 'minute')
    //             .format('HH:mm');

    //         checkInTimes.push({
    //             id: dateInfo.id,
    //             date: dateInfo.date,
    //             type: 'date',
    //             from_time: dateInfo.from_time,
    //             to_time: dateInfo.to_time,
    //             checkInTime,
    //             checkInStart: dayjs(dateInfo.date, 'DD/MM/YYYY')
    //                 .format('YYYY-MM-DD') + ' ' + checkInTime + ':00'
    //         });
    //     });

    //     // Process slots from ticketValidSlots
    //     formik.values.ticketValidSlots?.forEach(slot => {
    //         if (!slot || !slot.from_time) return;

    //         const checkInTme = dayjs(slot.from_time, 'HH:mm')
    //             .subtract(30, 'minute')
    //             .format('HH:mm');

    //         checkInTimes.push({
    //             id: slot.id,
    //             date: slot.date,
    //             type: 'slot',
    //             from_time: slot.from_time,
    //             to_time: slot.to_time,
    //             checkInTime: dayjs(slot.from_time, 'HH:mm')
    //                 .subtract(30, 'minute')
    //                 .format('HH:mm'),
    //             checkInStart: dayjs(slot.date, 'YYYY-MM-DD')
    //                 .format('YYYY-MM-DD') + ' ' + checkInTme + ':00'
    //         });
    //     });

    //     // Update formik
    //     formik.setFieldValue('checkInTimes', checkInTimes);

    // }, [formik.values.ticketValidDates, formik.values.ticketValidSlots]);


    /**
     * @function
     * @description Handles the selection of check-in time for a specific date or slot.
     * @param {number|string} id - The ID of the date or slot for which the check-in time is being set.
     * @param {dayjs.Dayjs} value - The selected check-in time as a Dayjs object.
     * @returns {void}
     * 1. Finds the index of the item in formik.values.checkInTimes that matches the provided id.
     * 2. If the item is found, it clones the current checkInTimes array to avoid direct mutation.
     * 3. Parses the provided value to extract the time in 'HH:mm' format.
     * 4. Determines the date format of the existing item's date and parses it accordingly.
     * 5. Constructs a combined datetime string in 'YYYY-MM-DD HH:mm:ss' format.
     * 6. Updates the corresponding item in the cloned array with the new check-in time and combined datetime.
     * 7. Updates the formik field 'checkInTimes' with the modified array.
     */
    function handleCheckInTimeSelection(id, value) {

        const index = formik.values.checkInTimes.findIndex(item => item.id === id);

        if (index !== -1) {
            const updatedCheckInTimes = [...formik.values.checkInTimes];
            const existingItem = updatedCheckInTimes[index];

            if (!value) return;

            const newTime = dayjs(value).format('HH:mm');

            let parsedDate;

            if (/^\d{4}-\d{2}-\d{2}$/.test(existingItem.date)) {
                parsedDate = dayjs(existingItem.date, 'YYYY-MM-DD');
            }
            else if (/^\d{2}\/\d{2}\/\d{4}$/.test(existingItem.date)) {
                parsedDate = dayjs(existingItem.date, 'DD/MM/YYYY');
            }
            else {
                parsedDate = dayjs(existingItem.date);
            }

            const combinedDatetime = parsedDate.format('YYYY-MM-DD') + ' ' + newTime + ':00';

            updatedCheckInTimes[index] = {
                ...existingItem,
                checkInTime: newTime,
                checkInStart: combinedDatetime
            };

            formik.setFieldValue('checkInTimes', updatedCheckInTimes);
        }
    }


    // console.log("Formik -> ", formik.values)

    return {
        formik,
        mappedDates,
        mappedSlots,
        handleDateChange,
        handleDateSubmission,
        showDatesInInput,
        isDateDropdownOpen,
        setIsDateDropdownOpen,
        isSelectAllDates,
        setIsSelectAllDates,
        isSlotDropdownOpen,
        minStartTime,
        maxStartTime,
        minEndTime,
        maxEndTime,
        maxRangeDate,
        minRangeDate,
        handleSlotSubmission,
        handleSlotChange,
        onDateDropdownOpen,
        onSlotDropdownOpen,
        handleAllDatesSelect,
        handleAllSlotsSelect,
        preventBackSpaceKey,
        setShowDatesInInput,
        setShowSlotsInInput,
        setIsSelectAllSlots,
        showSlotsInInput,
        handleCancelTicketCreation,
        isEditModeOn,
        setIsEditModeOn,
        shouldShowEditButton,
        exceedStatusEditable,
        qtyEditable,
        handleEditing,
        shouldAllFieldDisabled,
        gstRates,
        eventData,
        handleCheckInTimeSelection,
        openPaidTicketCheckModal,
        missingDetailsForPaidTicket,
        handlePaidTicketContinue,
        setOpenPaidTicketCheckModal
    };
}